import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link } from 'react-router-dom';

import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { Currency } from '@/data/currencies/type';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { AccountDetailsModal } from '@/Screens/Dashboard/Home/AccountDetailsModal';
import { DetailedBalance } from '@/types/bankInfo';
import { Locale } from '@/types/locale';

interface Props {
  readonly balance: DetailedBalance;
  readonly showDetailsButton?: boolean;
  readonly beneficiaryId?: string;
  readonly label?: string;
  readonly variant?: 'filled' | 'outlined';
  readonly actionLabel?: string;
  readonly actionHref?: string;
  readonly showNativeIcon?: boolean;
}

export const BalanceWidget: React.FC<Props> = (props: Props): React.ReactElement => {
  const [isDetailsOpen, setIsDetailsOpen] = React.useState<boolean>(false);
  const {
    balance,
    showDetailsButton = true,
    beneficiaryId,
    label,
    variant = 'filled',
    actionLabel,
    actionHref,
    showNativeIcon = false,
  } = props;

  const formatter = useCurrencyFormatter();
  const formattedBalance = React.useMemo(
    (): string => formatter.format(balance.available, balance.currencyCode),
    [balance.currencyCode, balance.available, formatter],
  );
  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();
  const effectiveDisplayUsdcAsUsd = showNativeIcon ? false : displayUsdcAsUsd;
  const currency = React.useMemo(
    (): Currency => Currency.fromCode(locale, balance.currencyCode, effectiveDisplayUsdcAsUsd),
    [balance.currencyCode, locale, effectiveDisplayUsdcAsUsd],
  );

  const Icon = React.useMemo(
    (): React.FunctionComponent<React.SVGProps<SVGSVGElement>> => currency.icon,
    [currency.icon],
  );

  const onShowDetails = React.useCallback((): void => {
    setIsDetailsOpen(true);
  }, []);

  const onCloseDetails = React.useCallback((): void => {
    setIsDetailsOpen(false);
  }, []);

  return (
    <Box
      display="flex"
      alignItems="flex-end"
      justifyContent="space-between"
      bgcolor={variant === 'filled' ? 'custom.Primary1Background' : undefined}
      border={variant === 'outlined' ? 1 : undefined}
      borderColor={variant === 'outlined' ? 'custom.DividerLight' : undefined}
      borderRadius={2}
      padding={3}>
      <Box display="flex" alignItems="center" gap={3}>
        <Icon width={32} height="100%" />
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="h7" color="custom.LightGray">
            {label ?? tr('BALANCE')}
          </Typography>
          <Typography fontSize={24} fontWeight={400} lineHeight="120%" letterSpacing="-1%">
            {formattedBalance}
          </Typography>
        </Box>
      </Box>

      {actionHref && actionLabel && (
        <Button component={Link} to={actionHref} variant="light" size="compact">
          {actionLabel}
        </Button>
      )}

      {showDetailsButton && !actionHref && (
        <Button variant="light" size="compact" onClick={onShowDetails}>
          {tr('SEE_DETAILS')}
        </Button>
      )}

      <Dialog open={isDetailsOpen} onClose={onCloseDetails}>
        <ApplicationModal title={tr('DEPOSIT_DETAILS')} onClose={onCloseDetails}>
          <AccountDetailsModal currency={currency.code} beneficiaryId={beneficiaryId} />
        </ApplicationModal>
      </Dialog>
    </Box>
  );
};
